import type { ReactNode } from 'react';
import { Link } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core';
import { visuallyHidden } from '../visuallyHidden';
import {
  ageOf,
  MARK_COLOUR,
  MARK_SHAPE,
  MARK_WORD,
  severityCounts,
  SEVERITY_COLOUR,
  SEVERITY_SHAPE,
  VERDICT_MARKS,
  type Finding,
  type LayerCell,
  type Severity,
  type ShapeName,
  type SourceInfo,
  type VerdictMark,
} from './security';

/**
 * The pieces the Security page and a component's Security tab share
 * (specs/security-findings.md), so a finding reads the same on both: its
 * severity or verdict in words beside its colour, its facts, its age.
 */

const useStyles = makeStyles(theme => ({
  pills: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '1px 8px',
    border: '1px solid',
    borderRadius: 10,
    background: 'transparent',
    color: 'var(--ui-text)',
    fontSize: '0.8rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  // The fill weakens with the severity — solid, tinted, outlined, muted — so
  // the order reads without telling one red from another.
  critical: {
    background: 'var(--ui-sev-critical)',
    borderColor: 'var(--ui-sev-critical)',
    color: 'var(--ui-sev-critical-ink)',
  },
  high: {
    background: 'var(--ui-sev-high-tint)',
    borderColor: 'var(--ui-sev-high)',
  },
  medium: { borderColor: 'var(--ui-sev-medium)' },
  low: {
    borderColor: 'var(--ui-border)',
    color: 'var(--ui-text-muted)',
    fontWeight: 500,
  },
  // Drawn rather than written: a font's own ▲ ◆ ● come in different sizes,
  // and the word beside the shape is what is read, by eye and by a screen reader.
  shape: {
    display: 'inline-block',
    width: '0.7em',
    height: '0.7em',
    flex: '0 0 auto',
    verticalAlign: '-0.05em',
  },
  dot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    flex: '0 0 auto',
  },
  markCount: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    marginRight: theme.spacing(1),
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  muted: { color: 'var(--ui-text-muted)', fontWeight: 400 },
  none: { whiteSpace: 'nowrap' },
  check: { color: 'var(--ui-sev-none)', fontWeight: 700, marginRight: 4 },
  verdict: { display: 'flex', alignItems: 'baseline', gap: 6 },
  caveat: {
    display: 'block',
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
  },
  tableBox: {
    position: 'relative',
    overflowX: 'auto',
    minWidth: 0,
  },
  table: {
    width: '100%',
    minWidth: 560,
    borderCollapse: 'collapse',
    fontSize: '0.875rem',
    color: 'var(--ui-text)',
    '& th, & td': {
      padding: theme.spacing(1, 1.5),
      borderBottom: '1px solid var(--ui-border)',
      textAlign: 'left',
      verticalAlign: 'top',
    },
    '& thead th': {
      fontWeight: 400,
      fontSize: '0.8rem',
      color: 'var(--ui-text-muted)',
    },
    '& tbody tr:last-child > *': { borderBottom: 'none' },
  },
  mono: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
  },
  notices: {
    margin: 0,
    padding: theme.spacing(1, 2),
    listStyle: 'none',
    border: '1px solid var(--ui-warn-border)',
    borderRadius: 8,
    background: 'var(--ui-warn-bg)',
    color: 'var(--ui-text)',
    fontSize: '0.875rem',
    lineHeight: 1.6,
  },
  hidden: visuallyHidden,
}));

/** A severity's or a verdict's shape, in its colour; read by its word beside it. */
export const Shape = ({
  shape,
  colour,
}: {
  shape: ShapeName;
  colour?: string;
}) => {
  const classes = useStyles();
  const stroke = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  return (
    <svg
      className={classes.shape}
      viewBox="0 0 10 10"
      style={colour ? { color: colour } : undefined}
      aria-hidden
      focusable="false"
    >
      {shape === 'triangle' && (
        <polygon points="5,0.6 9.6,9.2 0.4,9.2" fill="currentColor" />
      )}
      {shape === 'diamond' && (
        <polygon points="5,0.3 9.7,5 5,9.7 0.3,5" fill="currentColor" />
      )}
      {shape === 'circle' && (
        <circle cx="5" cy="5" r="4.3" fill="currentColor" />
      )}
      {shape === 'ring' && <circle cx="5" cy="5" r="3.8" {...stroke} />}
      {shape === 'check' && (
        <polyline points="1.2,5.4 3.9,8.1 8.8,1.9" {...stroke} />
      )}
    </svg>
  );
};

/** A severity in words, its colour beside it — "6 critical", or "critical". */
export const SeverityPill = ({
  severity,
  count,
}: {
  severity: Severity;
  count?: number;
}) => {
  const classes = useStyles();
  return (
    <span className={`${classes.pill} ${classes[severity]}`}>
      <Shape
        shape={SEVERITY_SHAPE[severity]}
        colour={severity === 'critical' ? undefined : SEVERITY_COLOUR[severity]}
      />
      {count === undefined ? severity : `${count} ${severity}`}
    </span>
  );
};

/** Open findings by severity, the most severe first. */
export const SeverityPills = ({
  bySeverity,
}: {
  bySeverity: Record<Severity, number>;
}) => {
  const classes = useStyles();
  return (
    <span className={classes.pills}>
      {severityCounts(bySeverity).map(({ severity, count }) => (
        <SeverityPill key={severity} severity={severity} count={count} />
      ))}
    </span>
  );
};

/** Verdicts counted by colour, each named for a screen reader. */
export const VerdictCounts = ({
  byMark,
  of,
}: {
  byMark: Partial<Record<VerdictMark, number>>;
  of?: number;
}) => {
  const classes = useStyles();
  return (
    <span className={classes.pills}>
      {VERDICT_MARKS.filter(mark => byMark[mark]).map(mark => (
        <span key={mark} className={classes.markCount} title={MARK_WORD[mark]}>
          <Shape shape={MARK_SHAPE[mark]} colour={MARK_COLOUR[mark]} />
          {byMark[mark]}
          <span className={classes.hidden}> {MARK_WORD[mark]}</span>
        </span>
      ))}
      {of !== undefined && <span className={classes.muted}>of {of}</span>}
    </span>
  );
};

/** "✓ None open": read, and nothing is. */
export const NoneOpen = () => {
  const classes = useStyles();
  return (
    <span className={classes.none}>
      <span className={classes.check} aria-hidden>
        ✓
      </span>
      None open
    </span>
  );
};

/** "? Not measured": a source covers it and could not read it — never a 0. */
export const NotMeasured = ({ reasons }: { reasons: string[] }) => {
  const classes = useStyles();
  return (
    <span className={classes.muted} title={reasons.join(' · ') || undefined}>
      <b aria-hidden>? </b>
      Not measured
      {reasons.length > 0 && (
        <span className={classes.hidden}>: {reasons.join('; ')}</span>
      )}
    </span>
  );
};

/** One layer of one component, as the page's table and the tab's figures show it. */
export const LayerCellView = ({ cell }: { cell: LayerCell }) => {
  const classes = useStyles();
  if (cell.state === 'uncovered') {
    return (
      <span className={classes.muted}>
        <span aria-hidden>—</span>
        <span className={classes.hidden}>No source covers it</span>
      </span>
    );
  }
  if (cell.state === 'unmeasured')
    return <NotMeasured reasons={cell.reasons} />;
  if (cell.open === 0) return <NoneOpen />;
  if (Object.keys(cell.byMark).length > 0) {
    return (
      <VerdictCounts
        byMark={cell.byMark}
        of={cell.measured + cell.notMeasured}
      />
    );
  }
  return <SeverityPills bySeverity={cell.bySeverity} />;
};

/** A finding's severity, or its verdict as the report words it. */
const Weight = ({ finding }: { finding: Finding }) => {
  const classes = useStyles();
  if (finding.verdict) {
    return (
      <span>
        <span className={classes.verdict}>
          <Shape
            shape={MARK_SHAPE[finding.verdict.mark]}
            colour={MARK_COLOUR[finding.verdict.mark]}
          />
          {finding.verdict.text}
        </span>
        {finding.verdict.caveat && (
          <span className={classes.caveat}>
            Vorbehalt: {finding.verdict.caveat}
          </span>
        )}
      </span>
    );
  }
  return finding.severity ? <SeverityPill severity={finding.severity} /> : null;
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  });

/**
 * One source's findings as a table whose columns the source names: its
 * severity or verdict, its title, its own facts, its date and a link.
 */
export const FindingsTable = ({
  source,
  findings,
  notMeasured = [],
  now,
  label,
}: {
  source: SourceInfo;
  findings: Finding[];
  notMeasured?: { title: string; identifier: string }[];
  now: number;
  label: string;
}) => {
  const classes = useStyles();
  const factLabels = [
    ...new Set(findings.flatMap(f => f.facts.map(fact => fact.label))),
  ];
  const verdicts = findings.some(f => f.verdict);
  const columns = 4 + factLabels.length;
  return (
    <div className={classes.tableBox}>
      <table className={classes.table} aria-label={label}>
        <thead>
          <tr>
            <th scope="col">{verdicts ? 'Verdict' : 'Severity'}</th>
            <th scope="col">{source.titleLabel}</th>
            {factLabels.map(fact => (
              <th key={fact} scope="col">
                {fact}
              </th>
            ))}
            <th scope="col">{source.sinceLabel}</th>
            <th scope="col">
              <span className={classes.hidden}>Link</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {/* One advisory can be open in several manifests of a repository,
              so its identifier alone is not unique. */}
          {findings.map((finding, i) => (
            <tr key={`${finding.source}:${finding.identifier}:${i}`}>
              <td>
                <Weight finding={finding} />
              </td>
              <th scope="row" style={{ fontWeight: 400 }}>
                {finding.title}
              </th>
              {factLabels.map(fact => (
                <td key={fact} className={classes.mono}>
                  {finding.facts.find(f => f.label === fact)?.value ?? ''}
                </td>
              ))}
              <td>
                {finding.openSince ? (
                  <time dateTime={finding.openSince} title={finding.openSince}>
                    {finding.verdict
                      ? formatDate(finding.openSince)
                      : ageOf(finding.openSince, now)}
                  </time>
                ) : (
                  ''
                )}
              </td>
              <td>
                {finding.link && (
                  <Link
                    to={finding.link}
                    aria-label={`${finding.title} at ${source.label}`}
                  >
                    View
                  </Link>
                )}
              </td>
            </tr>
          ))}
          {/* A node and a layer can share a slug, so the slug alone is not
              unique either. */}
          {notMeasured.map((item, i) => (
            <tr key={`not-measured:${item.identifier}:${i}`}>
              <td className={classes.muted}>Not analysed yet</td>
              <th scope="row" style={{ fontWeight: 400 }}>
                {item.title}
              </th>
              <td colSpan={columns - 2} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** The sentences above a table saying what is not counted, and why. */
export const Notices = ({
  notices,
  label = 'Not counted',
}: {
  notices: ReactNode[];
  label?: string;
}) => {
  const classes = useStyles();
  if (notices.length === 0) return null;
  return (
    <ul className={classes.notices} aria-label={label}>
      {notices.map((notice, i) => (
        <li key={i}>{notice}</li>
      ))}
    </ul>
  );
};
